import { useState, useRef, useCallback } from 'react';
import { ApiError } from '@/lib/api/api-helpers';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const GENERATION_TIMEOUT_MS = 120000; // 2 minutes — reasoning/thinking models can take 60-120s
const VIDEO_GENERATION_TIMEOUT_MS = 300000; // 5 minutes — video models take 60-300s

interface GenerateTextRequest {
  prompt: string;
  model?: string;
  quantity?: number;
  systemPrompt?: string;
  parameters?: Record<string, unknown>;
  image?: string | string[];
}

interface StreamCallbacks {
  onToken: (token: string) => void;
  onProgress?: (progress: number) => void;
  onDone: (variant: { id: string; text: string; model: string; provider: string; createdAt: string }) => void;
  onError?: (error: string) => void;
}

interface GenerateTextResponse {
  success: boolean;
  variants: Array<{
    id: string;
    text: string;
    model: string;
    provider: string;
    createdAt: string;
  }>;
  metadata: {
    totalRequested: number;
    successCount: number;
    failureCount: number;
  };
  usedParameters: {
    model: string;
    parameters: Record<string, unknown>;
  };
}

interface GenerateImageRequest {
  prompt: string;
  model?: string;
  quantity?: number;
  parameters?: Record<string, unknown>;
  image?: string | string[];
  images?: string[];
  mask?: string;
}

interface GenerateImageResponse {
  success: boolean;
  variants: Array<{
    id: string;
    url: string;
    model: string;
    provider: string;
    createdAt: string;
    metadata?: Record<string, unknown>;
  }>;
  metadata: {
    totalRequested: number;
    successCount: number;
    failureCount: number;
  };
  usedParameters: {
    model: string;
    parameters: Record<string, unknown>;
  };
}

export function useAIGeneration() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Track all active stream controllers for parallel abort
  const activeStreamsRef = useRef<Set<AbortController>>(new Set());

  const generateText = async (
    request: GenerateTextRequest
  ): Promise<GenerateTextResponse> => {
    setLoading(true);
    setError(null);

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), GENERATION_TIMEOUT_MS);

    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/text/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(request),
        signal: controller.signal,
      });

      if (!response.ok) {
        let errorData: { error?: string; code?: string } = {};
        try {
          const contentType = response.headers.get('content-type');
          if (contentType?.includes('application/json')) {
            errorData = await response.json();
          }
        } catch {
          // Non-JSON error response (HTML error page, etc.)
        }
        throw new ApiError(
          errorData.error || `Generation failed (${response.status})`,
          response.status,
          errorData.code || 'GENERATION_ERROR',
        );
      }

      const result = await response.json();
      return result;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        const timeoutError = new Error('Generation timed out. Please try again.');
        setError(timeoutError.message);
        throw timeoutError;
      }
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMsg);
      throw err;
    } finally {
      clearTimeout(timeoutId);
      abortControllerRef.current = null;
      setLoading(false);
    }
  };

  const generateImage = async (
    request: GenerateImageRequest
  ): Promise<GenerateImageResponse> => {
    setLoading(true);
    setError(null);

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), GENERATION_TIMEOUT_MS);

    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/image/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(request),
        signal: controller.signal,
      });

      if (!response.ok) {
        let errorData: { error?: string; code?: string } = {};
        try {
          const contentType = response.headers.get('content-type');
          if (contentType?.includes('application/json')) {
            errorData = await response.json();
          }
        } catch {
          // Non-JSON error response (HTML error page, etc.)
        }
        throw new ApiError(
          errorData.error || `Generation failed (${response.status})`,
          response.status,
          errorData.code || 'GENERATION_ERROR',
        );
      }

      const result = await response.json();
      return result;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        const timeoutError = new Error('Generation timed out. Please try again.');
        setError(timeoutError.message);
        throw timeoutError;
      }
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMsg);
      throw err;
    } finally {
      clearTimeout(timeoutId);
      abortControllerRef.current = null;
      setLoading(false);
    }
  };

  const generateVideo = async (
    request: GenerateImageRequest
  ): Promise<GenerateImageResponse> => {
    setLoading(true);
    setError(null);

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), VIDEO_GENERATION_TIMEOUT_MS);

    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/video/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(request),
        signal: controller.signal,
      });

      if (!response.ok) {
        let errorData: { error?: string; code?: string } = {};
        try {
          const contentType = response.headers.get('content-type');
          if (contentType?.includes('application/json')) {
            errorData = await response.json();
          }
        } catch {
          // Non-JSON error response (HTML error page, etc.)
        }
        throw new ApiError(
          errorData.error || `Generation failed (${response.status})`,
          response.status,
          errorData.code || 'GENERATION_ERROR',
        );
      }

      const result = await response.json();
      return result;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        const timeoutError = new Error('Generation timed out. Please try again.');
        setError(timeoutError.message);
        throw timeoutError;
      }
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMsg);
      throw err;
    } finally {
      clearTimeout(timeoutId);
      abortControllerRef.current = null;
      setLoading(false);
    }
  };

  const generateTextStream = async (
    request: GenerateTextRequest,
    callbacks: StreamCallbacks
  ): Promise<void> => {
    setLoading(true);
    setError(null);

    // Each stream gets its own controller (parallel calls must not abort each other)
    const controller = new AbortController();
    activeStreamsRef.current.add(controller);
    const timeoutId = setTimeout(() => controller.abort(), GENERATION_TIMEOUT_MS);

    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/text/generate/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(request),
        signal: controller.signal,
      });

      if (!response.ok) {
        let errorData: { error?: string } = {};
        try {
          const ct = response.headers.get('content-type');
          if (ct?.includes('application/json')) {
            errorData = await response.json();
          }
        } catch {
          // Non-JSON error response
        }
        throw new ApiError(
          errorData.error || `Stream failed (${response.status})`,
          response.status,
          'STREAM_ERROR',
        );
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';
      let completed = false;

      const processLine = (line: string) => {
        if (!line.startsWith('data:')) return;
        const data = line.slice(5).trim();
        if (!data || data === '[DONE]') return;
        let event: Record<string, unknown>;
        try { event = JSON.parse(data); } catch { return; }
        switch (event.type) {
          case 'token':
            callbacks.onToken(event.content as string);
            break;
          case 'progress':
            callbacks.onProgress?.(event.progress as number);
            break;
          case 'done':
            callbacks.onDone(event.variant as { id: string; text: string; model: string; provider: string; createdAt: string });
            completed = true;
            break;
          case 'error':
            throw new Error(typeof event.message === 'string' ? event.message : 'Text generation failed.');
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) processLine(line);
      }
      processLine(buffer + decoder.decode());
      if (!completed) throw new Error('The connection ended before text generation finished. Please retry.');
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        const msg = 'Generation timed out. Please try again.';
        setError(msg);
        callbacks.onError?.(msg);
        throw new Error(msg);
      }
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMsg);
      callbacks.onError?.(errorMsg);
      throw err;
    } finally {
      controller.abort();
      clearTimeout(timeoutId);
      activeStreamsRef.current.delete(controller);
      if (activeStreamsRef.current.size === 0) {
        setLoading(false);
      }
    }
  };

  const abort = useCallback(() => {
    // Abort all active streams
    for (const ctrl of activeStreamsRef.current) {
      ctrl.abort();
    }
    activeStreamsRef.current.clear();
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setLoading(false);
  }, []);

  return { generateText, generateTextStream, generateImage, generateVideo, loading, error, abort };
}
